import type { AnchorHTMLAttributes, MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router";

import { useLocale } from "../hooks/useLocale";

type HashLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  /** The hash target, e.g. "#features". May also be a full route like "/privacy". */
  to: string;
};

/**
 * Anchor that scrolls to an in-page hash, even from a sub-route. If we're
 * already on the home page it just lets the browser smooth-scroll. If we're
 * elsewhere it navigates home for the current locale and then scrolls after
 * paint.
 */
export function HashLink({ to, onClick, children, ...rest }: HashLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { code, pathFor } = useLocale();

  const isHash = to.startsWith("#");
  const isExternal = to.startsWith("http") || to.startsWith("mailto:");

  const href = isExternal
    ? to
    : isHash
      ? `${pathFor("/")}${to}`
      : pathFor(to);

  const onHomePath =
    location.pathname === `/${code}` || location.pathname === `/${code}/`;

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (onClick) onClick(event);
    if (event.defaultPrevented || isExternal || !isHash) return;
    event.preventDefault();
    const targetId = to.slice(1);
    const scrollToTarget = () => {
      const el = document.getElementById(targetId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (onHomePath) {
      scrollToTarget();
    } else {
      navigate(`${pathFor("/")}${to}`);
      // Wait a tick so the Home page mounts before scrolling.
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTarget);
      });
    }
  }

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
